import React, { useState, useEffect } from 'react';
import { 
  FiClock, 
  FiPlay, 
  FiPause, 
  FiTrash2, 
  FiEdit3, 
  FiEye,
  FiCalendar,
  FiMail,
  FiUsers,
  FiBarChart2,
  FiRefreshCw,
  FiFilter,
  FiSearch,
  FiPlus,
  FiActivity,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiAlertTriangle,
  FiUser,
  FiAward,
  FiFileText,
  FiTrendingUp,
  FiMousePointer,
  FiToggleLeft,
  FiToggleRight,
  FiPower
} from 'react-icons/fi';
import showToast from '../../components/Toast';
import axiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import historyTracker from '../../utils/historyTracker';
import EnhancedUniversalTable from '../../components/EnhancedUniversalTable';
import CustomDropdown from '../../components/CustomDropdown';
import './Cronjobs.css';
import globalRequestManager from '../../utils/globalRequestManager';

const Cronjobs = () => {
  const [cronjobs, setCronjobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedCronjob, setSelectedCronjob] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    totalItems: 0
  });
  const [sortConfig, setSortConfig] = useState({
    sortField: 'createdAt',
    sortOrder: 'desc'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  useEffect(() => {
    fetchCronjobs();
    fetchStats();
  }, [filters, pagination.current, sortConfig]);

  const fetchCronjobs = async () => {
    // Add minimum loading time to ensure loader shows properly
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));
    
    const params = new URLSearchParams({
      page: pagination.current,
      limit: 10,
      ...filters,
      sortField: sortConfig.sortField,
      sortOrder: sortConfig.sortOrder
    });
    
    const url = `${config.api.baseURL}/${config.api.apiPath}/emailcronjob?${params}`;
    
    try {
      setLoading(true);
      setCronjobs([]); // Clear cronjobs data when loading starts
      
      const result = await globalRequestManager.executeRequest(
        async () => {
          const response = await axiosInstance.get(url);
          
          if (response && response.data && response.data.success) {
            setCronjobs(response.data.cronjobs || []);
            setPagination(prev => ({
              ...prev,
              total: response.data.pagination?.total || 1,
              totalItems: response.data.pagination?.totalItems || 0
            }));
          } else {
            setCronjobs([]);
          }
          
          return response;
        },
        'GET',
        url,
        { page: pagination.current, limit: 10, ...filters, ...sortConfig }
      );
      
      if (result === null) {
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("Error fetching cronjobs data:", error.message || error);
      setCronjobs([]); // Ensure cronjobs is set to empty array on error
    } finally {
      // Wait for minimum loading time before setting loading to false
      await minLoadingTime;
      setLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchStats = async () => {
    const url = `${config.api.baseURL}/${config.api.apiPath}/emailcronjob/stats/overview`;
    
    const result = await globalRequestManager.executeRequest(
      async () => {
        const response = await axiosInstance.get(url);
        
        if (response && response.data && response.data.success) {
          setStats(response.data.stats || {});
        }
        
        return response;
      },
      'GET',
      url
    );
    
    if (result === null) {
      return;
    }
  };

  const handleCancelCronjob = async (cronjonId) => {
    try {
      const response = await axiosInstance.put(
        `${config.api.baseURL}/${config.api.apiPath}/emailCampaign/scheduled/${cronjonId}/cancel`
      );
      
      if (response && response.data && response.data.campaignId) {
        fetchCronjobs();
        fetchStats();
        showToast.success('Cronjob cancelled successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      showToast.error('Failed to cancel cronjob', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error cancelling cronjob:', error);
    }
  };

  const handleToggleActive = async (cronjonId) => {
    try {
      // Find the current campaign to get its current state
      const currentCampaign = cronjobs.find(campaign => campaign.cronjonId === cronjonId);
      if (!currentCampaign) return;

      // Optimistically update the UI immediately
      setCronjobs(prevCronjobs => 
        prevCronjobs.map(campaign => 
          campaign.cronjonId === cronjonId 
            ? { ...campaign, isActive: !campaign.isActive }
            : campaign
        )
      );

      const response = await axiosInstance.put(
        `${config.api.baseURL}/${config.api.apiPath}/emailcronjob/${cronjonId}/toggle`
      );
      
      if (response && response.data && response.data.success) {
        // Update with server response
        setCronjobs(prevCronjobs => 
          prevCronjobs.map(campaign => 
            campaign.cronjonId === cronjonId 
              ? { ...campaign, isActive: response.data.isActive }
              : campaign
          )
        );
        fetchStats();
        showToast.success(response.data.message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      // Revert the optimistic update on error
      setCronjobs(prevCronjobs => 
        prevCronjobs.map(campaign => 
          campaign.cronjonId === cronjonId 
            ? { ...campaign, isActive: !campaign.isActive }
            : campaign
        )
      );
      showToast.error('Failed to toggle campaign status', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error toggling campaign:', error);
    }
  };

  const viewCronjobDetails = async (cronjonId) => {
    try {
      const response = await axiosInstance.get(
        `${config.api.baseURL}/${config.api.apiPath}/emailcronjob/${cronjonId}`
      );
      
      if (response && response.data && response.data.success) {
        setSelectedCronjob(response.data.cronjob);
        setShowAnalyticsModal(true);
      }
    } catch (error) {
      showToast.error('Failed to fetch cronjob details', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error fetching cronjob details:', error);
    }
  };

  const handleDeleteCronjob = async (cronjonId) => {
    try {
      const response = await axiosInstance.delete(
        `${config.api.baseURL}/${config.api.apiPath}/emailcronjob/${cronjonId}`
      );
      
      if (response && response.data && response.data.success) {
        fetchCronjobs();
        fetchStats();
        setShowDeleteModal(false);
        setDeleteConfirmText('');
        setCampaignToDelete(null);
        showToast.success('Cronjob deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      showToast.error('Failed to delete cronjob', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error deleting cronjob:', error);
    }
  };

  const openDeleteModal = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
    setDeleteConfirmText('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setCampaignToDelete(null);
  };

  const getStatusIcon = (status, isRunning) => {
    if (isRunning) return <FiRefreshCw className="status-icon running" />;
    
    switch (status) {
      case 'scheduled':
        return <FiClock className="status-icon scheduled" />;
      case 'sent':
        return <FiCheckCircle className="status-icon sent" />;
      case 'cancelled':
        return <FiXCircle className="status-icon cancelled" />;
      default:
        return <FiAlertCircle className="status-icon default" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status, isRunning) => {
    if (isRunning) return 'running';
    return status;
  };

  // Handle sorting from EnhancedUniversalTable
  const handleSort = (sortField) => {
    setSortConfig(prevConfig => ({
      sortField,
      sortOrder: prevConfig.sortField === sortField && prevConfig.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when sorting
  };


  return (
    <div className="email-campaign-container">
      {/* Enhanced Header */}
      <div className="campaign-header">
        <div className="header-left">
          <h1>
            <FiClock />
            Scheduled Emails
          </h1>
        </div>
        <button 
        
          className="create-campaign-btn"
          onClick={() => {
            fetchCronjobs();
            fetchStats();
          }}
        >
          <FiRefreshCw />
          Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-container">
        <div className="stat-card total">
          <div className="stat-icon">
            <FiBarChart2 />
          </div>
          <div className="stat-content">
            <h3>{stats.total || 0}</h3>
            <p>Total Cronjobs</p>
          </div>
        </div>
        
        <div className="stat-card scheduled">
          <div className="stat-icon">
            <FiCalendar />
          </div>
          <div className="stat-content">
            <h3>{stats.scheduled || 0}</h3>
            <p>Scheduled</p>
          </div>
        </div>
        
        <div className="stat-card running">
          <div className="stat-icon">
            <FiPlay />
          </div>
          <div className="stat-content">
            <h3>{stats.running || 0}</h3>
            <p>Running</p>
          </div>
        </div>
        
        <div className="stat-card pending">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.pending || 0}</h3>
            <p>Pending</p>
          </div>
        </div>
      </div>


      {/* Main Table */}
      <div className="table-container">
        <EnhancedUniversalTable
          data={cronjobs}
          sortConfig={sortConfig}
          onSort={handleSort}
          columns={[
            {
              key: 'name',
              title: 'Campaign Name',
              sortable: true,
              width: '200px',
              minWidth: '180px',
              render: (value, row) => (
                <div className="campaign-name-cell" title={value}>
                  <FiMail className="campaign-icon" />
                  <span>{value}</span>
                </div>
              )
            },
            {
              key: 'subject',
              title: 'Subject',
              sortable: true,
              width: '150px',
              minWidth: '120px',
              render: (value) => (
                <span title={value}>{value}</span>
              )
            },
            {
              key: 'status',
              title: 'Status',
              sortable: true,
              width: '100px',
              minWidth: '80px',
              render: (value, row) => (
                <span className={`status-badge ${getStatusBadge(value, row.IsCronjob_running)}`}>
                  {row.IsCronjob_running ? 'Running' : value}
                </span>
              )
            },
            {
              key: 'isActive',
              title: 'Active Status',
              sortable: true,
              width: '120px',
              minWidth: '100px',
              render: (value, row) => {
                // Default to false if isActive is undefined/null
                const isActive = row.isActive !== undefined && row.isActive !== null ? row.isActive : false;
                
                return (
                  <div className="active-status-cell">
                    <div className="toggle-switch" onClick={() => handleToggleActive(row.cronjonId)}>
                      <div className={`toggle-slider ${isActive ? 'active' : 'inactive'}`}>
                        <div className="toggle-knob"></div>
                      </div>
                    </div>
                  </div>
                );
              }
            },
            {
              key: 'recipients',
              title: 'Recipients',
              sortable: true,
              width: '100px',
              minWidth: '80px',
              render: (value, row) => (
                <div className="recipients-cell">
                  <FiUsers />
                  <span>{row.totalRecipients || row.recipients?.length || 0}</span>
                </div>
              )
            },
            {
              key: 'scheduleDate',
              title: 'Scheduled',
              sortable: true,
              width: '180px',
              minWidth: '150px',
              render: (value) => (
                <div className="date-cell" title={formatDate(value)}>
                  <FiCalendar />
                  <span>{formatDate(value)}</span>
                </div>
              )
            },
            {
              key: 'sentCount',
              title: 'Sent',
              sortable: true,
              width: '80px',
              minWidth: '60px',
              render: (value) => value || 0
            }
          ]}
          actions={[
            {
              key: 'analytics',
              icon: <FiBarChart2 />,
              title: 'View Analytics',
              variant: 'primary'
            },
            {
              key: 'toggle',
              icon: <FiPower />,
              title: 'Toggle Status',
              variant: 'secondary',
              show: (row) => row.isActive !== undefined
            },
            {
              key: 'cancel',
              icon: <FiXCircle />,
              title: 'Cancel',
              variant: 'danger',
              show: (row) => row.status === 'scheduled' && !row.IsCronjob_running
            },
            {
              key: 'delete',
              icon: <FiTrash2 />,
              title: 'Delete',
              variant: 'danger'
            }
          ]}
          onActionClick={async (actionKey, row, rowIndex) => {
            switch (actionKey) {
              case 'analytics':
                viewCronjobDetails(row.cronjonId);
                break;
              case 'toggle':
                handleToggleActive(row.cronjonId);
                break;
              case 'cancel':
                handleCancelCronjob(row.cronjonId);
                break;
              case 'delete':
                openDeleteModal(row);
                break;
              default:
            }
          }}
          loading={loading || (!hasLoaded && cronjobs.length === 0)}
          searchable={true}
          filterable={true}
          sortable={true}
          pagination={true}
          pageSize={10}
          searchPlaceholder="Search cronjobs..."
          emptyMessage="No scheduled email campaigns found"
          striped={true}
          hover={true}
          responsive={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-header">
              <h3>Delete Campaign</h3>
              <button className="close-btn" onClick={closeDeleteModal}>
                <FiXCircle />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-content">
                <div className="left-section">
                  <div className="icon-container">
                    <div className="icon-background">
                      <FiAlertTriangle className="warning-icon" />
                    </div>
                    <div className="icon-pulse"></div>
                  </div>
                </div>
                <div className="right-section">
                  <div className="warning-content">
                    <div className="title-section">
                      <h4>⚠️ Delete Campaign</h4>
                      <div className="danger-badge">DANGER</div>
                    </div>
                    <div className="campaign-info">
                      <div className="campaign-name">
                        <span className="label">Campaign:</span>
                        <span className="name">"{campaignToDelete?.name}"</span>
                      </div>
                    </div>
                    <div className="warning-message">
                      <p>This action will permanently delete the campaign and all its associated data. This cannot be undone.</p>
                    </div>
                    <div className="confirmation-section">
                      <div className="confirm-label">
                        <span className="confirm-icon">🔒</span>
                        <span>Type <strong>DELETE</strong> to confirm this action:</span>
                      </div>
                      <div className="input-container">
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type DELETE here..."
                          className="delete-input"
                          autoFocus
                        />
                        <div className="input-indicator">
                          {deleteConfirmText === 'DELETE' ? '✅' : '❌'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button 
                className={`delete-btn ${deleteConfirmText === 'DELETE' ? 'enabled' : 'disabled'}`}
                onClick={() => handleDeleteCronjob(campaignToDelete.cronjonId)}
                disabled={deleteConfirmText !== 'DELETE'}
              >
                <FiTrash2 />
                Delete Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cronjobs;
